import React from "react";
import { motion } from "framer-motion";
import { useLanguage } from "@/context/LanguageContext";
import { commonLabels } from "@/i18n";

interface VideoTriggerButtonProps {
  onVideoOpen: () => void;
}

const VideoTriggerButton = ({ onVideoOpen }: VideoTriggerButtonProps) => {
  const { language } = useLanguage();
  const labels = commonLabels(language);
  const videoBtn =
    (labels.menuPages?.howItWorks?.hero as { videoButton?: { title?: string; subtitle?: string } })
      ?.videoButton ?? {};
  const title = videoBtn.title ?? "Se hur det fungerar";
  const subtitle = videoBtn.subtitle ?? "";

  return (
    <motion.button
      type="button"
      className="relative cursor-pointer inline-block w-[min(100%,22rem)] sm:w-auto text-left rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent mx-auto"
      onClick={onVideoOpen}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      aria-label={subtitle ? `${title}. ${subtitle}` : title}
    >
      <div
        className={[
          "relative transition-all duration-300",
          "rounded-2xl",
          "p-3.5 sm:p-5",
          "bg-gradient-to-br from-cyan-600/90 to-blue-700/90",
          "backdrop-blur-sm",
          "border border-cyan-400/35",
          "shadow-lg shadow-black/25",
          "hover:border-cyan-300/55 hover:shadow-cyan-500/15",
        ].join(" ")}
      >
        <div className="flex items-center gap-3 sm:gap-4">
          <div
            className={[
              "rounded-full flex items-center justify-center shrink-0",
              "bg-white/15",
              "w-12 h-12 sm:w-14 sm:h-14",
            ].join(" ")}
          >
            <svg
              className="text-white ml-0.5 w-6 h-6 sm:w-7 sm:h-7"
              fill="currentColor"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
          <div className="text-left min-w-0">
            <span className="text-white font-semibold text-base sm:text-lg block leading-snug">
              {title}
            </span>
            {subtitle ? (
              <span className="text-cyan-100/95 text-xs sm:text-sm block mt-1">
                {subtitle}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </motion.button>
  );
};

export default VideoTriggerButton;
